import { useEffect, useState } from 'react';

import { checkDesktopPresence, loopbackPermission } from './desktopPresence';

/** Check once when the signed-in page loads, and again only if its account changes. */
export function useDesktopPresence(sub?: string, enabled = true) {
  const [result, setResult] = useState<{ sub: string; available: boolean }>();
  useEffect(() => {
    setResult(undefined);
    if (!enabled || !sub) return;
    const controller = new AbortController();
    let active = true;
    const unavailable = () => {
      if (active) setResult({ sub, available: false });
      controller.abort();
    };
    // Bound the whole check, including permission lookup, so web Jot cannot stay hidden forever.
    let timeout = setTimeout(unavailable, 2000);
    const check = async () => {
      try {
        const permission = await loopbackPermission();
        if (controller.signal.aborted) return;
        if (permission === 'denied') {
          unavailable();
          return;
        }
        // Allow time for the browser's own permission prompt; Jot adds no setup step.
        if (permission === 'prompt') {
          clearTimeout(timeout);
          timeout = setTimeout(unavailable, 30000);
        }
        const available = await checkDesktopPresence(sub, controller.signal);
        if (!controller.signal.aborted) setResult({ sub, available });
      } catch {
        unavailable();
      } finally {
        clearTimeout(timeout);
      }
    };
    void check();
    return () => {
      active = false;
      controller.abort();
      clearTimeout(timeout);
    };
  }, [sub, enabled]);
  return {
    checking: enabled && Boolean(sub) && result?.sub !== sub,
    available: enabled && Boolean(sub) && result?.sub === sub && result?.available === true,
  };
}
